import { Activity, FC, ReactNode, useState } from 'react';
import styled from 'styled-components';

import { SlidesThemeSelector } from '../../../pages/Presentations/components/SlidesThemeSelector';
import { SlidesFiltersSelector } from '../../../pages/Presentations/components/SlidesFiltersSelector';
import {
  DEFAULT_SLIDES_FILTERS,
  FolderManagerSlidesViewFilters,
  isDefaultFilters,
  SlidesViewFilters,
} from './FolderManagerSlidesViewFilters';

const StyledContainerWrapper = styled.div`
  container-type: inline-size;
`;

const StyledHeaderRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};

  @container (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StyledLeftItems = styled.div`
  min-width: 0;
`;

const StyledControlsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.small};
`;

const StyledThemeSelectorWrapper = styled.div`
  flex-shrink: 0;
  width: 220px;
  max-width: 100%;
`;

const StyledClearFiltersButton = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primaryButton};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

interface SlidesControlBarProps {
  leftItems?: ReactNode;
  showControls?: boolean;
  filters: SlidesViewFilters;
  defaultFilters?: SlidesViewFilters;
  onFiltersChange?: (filters: SlidesViewFilters) => void;
  filtersExpanded?: boolean;
  onFiltersExpandedChange?: (expanded: boolean) => void;
  viewStateKey?: string;
  selectedThemeId?: number | null;
  defaultThemeId?: number | null;
  isLoadingDefault?: boolean;
  slideRepositoryId?: number;
  onThemeChange?: (theme: any) => void;
}

export const SlidesControlBar: FC<SlidesControlBarProps> = ({
  leftItems,
  showControls = true,
  filters,
  defaultFilters,
  onFiltersChange = () => {},
  filtersExpanded: filtersExpandedProp,
  onFiltersExpandedChange,
  viewStateKey,
  selectedThemeId,
  defaultThemeId,
  isLoadingDefault,
  slideRepositoryId,
  onThemeChange,
}) => {
  const [internalFiltersExpanded, setInternalFiltersExpanded] = useState(false);
  const filtersExpanded = filtersExpandedProp ?? internalFiltersExpanded;

  const handleToggleFilters = () => {
    const next = !filtersExpanded;
    onFiltersExpandedChange ? onFiltersExpandedChange(next) : setInternalFiltersExpanded(next);
  };

  const hasActiveFilters = !isDefaultFilters(filters, defaultFilters);
  const handleClearFilters = () => onFiltersChange(defaultFilters ?? DEFAULT_SLIDES_FILTERS);

  return (
    <>
      <StyledContainerWrapper>
        <StyledHeaderRow>
          <StyledLeftItems>{leftItems}</StyledLeftItems>

          {showControls && (
            <StyledControlsRow>
              {hasActiveFilters && (
                <StyledClearFiltersButton type="button" onClick={handleClearFilters}>
                  Clear filters
                </StyledClearFiltersButton>
              )}
              
              <SlidesFiltersSelector
                filters={filters}
                defaultFilters={defaultFilters}
                active={filtersExpanded}
                onClick={handleToggleFilters}
              />

              <StyledThemeSelectorWrapper>
                <SlidesThemeSelector
                  selectedThemeId={selectedThemeId}
                  defaultThemeId={defaultThemeId}
                  isLoadingDefault={isLoadingDefault}
                  viewStateKey={viewStateKey}
                  slideRepositoryId={slideRepositoryId}
                  compact
                  onChange={onThemeChange}
                />
              </StyledThemeSelectorWrapper>
            </StyledControlsRow>
          )}
        </StyledHeaderRow>
      </StyledContainerWrapper>

      {showControls && (
        <Activity mode={filtersExpanded ? 'visible' : 'hidden'}>
          <FolderManagerSlidesViewFilters
            filters={filters}
            onChange={onFiltersChange}
            showThemePicker={false}
            inline
            compact
          />
        </Activity>
      )}
    </>
  );
};
